/**
 * Source-of-truth GBP display pricing for the Insight plans (Basic/Pro +
 * prepaid AI credits migration).
 *
 * These are display prices only — Lemon Squeezy charges settle in USD, so the
 * LS variant prices must be kept FX-consistent with these figures. Basic is
 * the full app without recurring AI (a one-time starter credit is included);
 * Pro carries the weekly AI allowance, the report quota, and the ability to
 * top up AI credits. Usage remains percentage-only; credit balances display
 * as credit points.
 */

export type BillingInterval = "monthly" | "yearly";

/**
 * The native app sells PRO ONLY, on two terms (Ben, 2026-09-03: back to a
 * standard monthly + yearly, both with a free trial, while the store pages
 * and onboarding are still being tuned). Basic stays a web/organic plan:
 * tier choice is friction on store traffic.
 *
 * Yearly is priced so it reads as the obvious choice next to monthly (the
 * saving is always derived from store prices, never hardcoded).
 */
export type NativeBillingInterval = "monthly" | "annual";

export interface NativePlan {
  interval: NativeBillingInterval;
  label: string;
  /** Months billed per term — drives the per-month display and the saving. */
  months: number;
  /**
   * Fallback display price until RevenueCat store pricing loads. Store prices
   * carry the native markup (Apple's commission is priced in), so these
   * figures only ever surface in dev.
   */
  price: number;
  rcProductId: string | undefined;
}

export const NATIVE_PLANS: NativePlan[] = [
  {
    interval: "monthly",
    label: "Monthly",
    months: 1,
    price: 9.99,
    rcProductId: process.env.REACT_APP_RC_PRODUCT_ID_PRO_MONTHLY,
  },
  {
    interval: "annual",
    label: "Yearly",
    months: 12,
    price: 74.99,
    rcProductId: process.env.REACT_APP_RC_PRODUCT_ID_PRO_ANNUAL,
  },
];

/**
 * The exit-intent offer (native only): an annual product carrying a
 * pay-up-front introductory price for the first year, renewing at the full
 * sticker. Sold ONLY from the paywall's exit sheet — deliberately NOT a
 * NATIVE_PLANS row, so it can never render as a paywall option. No fallback
 * prices exist here: the sheet displays store figures or doesn't show at all,
 * because a displayed discount the payment sheet doesn't honour is an App
 * Store 3.1.2 problem.
 *
 * WHICH product the sheet sells is decided by the RevenueCat offering at
 * runtime (`offeringId` — swap the package in the RC dashboard and installed
 * builds re-target with no release). `rcProductId` is only the Account-label
 * mapping below. The offering must only ever hold ANNUAL products: the
 * sheet's "first year" wording is baked into the binary.
 */
export const EXIT_OFFER = {
  offeringId: "exit_offer",
  months: 12,
  rcProductId: process.env.REACT_APP_RC_PRODUCT_ID_PRO_ANNUAL_OFFER,
};

export interface PricingTier {
  key: "basic" | "pro";
  name: string;
  tagline: string;
  monthly: number;
  yearly: number;
  /**
   * Display per-month equivalent for annual billing. yearly/12 gives awkward
   * figures, so inexact tiers use the nearest clean price instead (pennies
   * off at most). The exact annual total is always shown alongside.
   */
  yearlyPerMonth: number;
  features: string[];
  /**
   * Both web plans carry the same 7-day free trial for first-time members.
   * Must match the trial configuration on the same environment's Lemon
   * Squeezy variants.
   */
  hasTrial: boolean;
  /** Higher = higher plan; drives upgrade labelling. */
  rank: number;
  variantIds: Record<BillingInterval, string | undefined>;
}

/**
 * Basic's screener sessions a month, for plan copy that can't read the API's
 * allowance. Must equal the API's SCREENER_BASIC_MONTHLY_RUNS
 * (config/screener.ts); the API's screenerPlanCopy.test.ts checks it.
 */
export const BASIC_SCREENER_SESSIONS = 3;

export const PRICING_TIERS: PricingTier[] = [
  {
    key: "basic",
    name: "Basic",
    tagline: "The full research toolkit",
    monthly: 6.99,
    yearly: 69,
    yearlyPerMonth: 5.75,
    // Not "every feature": the screener caps Basic's sessions, and saving
    // screens and CSV export are Pro only.
    features: [
      "Financials, earnings, insider trades and watchlists",
      `${BASIC_SCREENER_SESSIONS} screener sessions a month, 24 hours each`,
      "One-time AI starter credit included",
      "Upgrade to Pro any time for weekly AI",
    ],
    hasTrial: true,
    rank: 0,
    variantIds: {
      monthly: process.env.REACT_APP_LS_VARIANT_ID_BASIC_MONTHLY,
      yearly: process.env.REACT_APP_LS_VARIANT_ID_BASIC_YEARLY,
    },
  },
  {
    key: "pro",
    name: "Pro",
    tagline: "Everything you need to invest smarter",
    monthly: 9.99,
    yearly: 99,
    yearlyPerMonth: 8.25,
    // The screener lines must track the API: SCREENER_BASIC_MONTHLY_RUNS and
    // SCREENER_MAX_SAVED_SCREENS.pro (config/screener.ts). CSV export is web
    // only, and so are these tiers (native sells from NATIVE_PRO_FEATURES).
    features: [
      "Everything in Basic",
      "Unlimited stock screening",
      "Save up to 10 screens",
      "Daily alerts when new stocks match your screens",
      "Export screen results to CSV",
      "AI chat every week",
      "4 AI stock reports a week",
      "Top up AI credits any time",
    ],
    hasTrial: true,
    rank: 1,
    variantIds: {
      monthly: process.env.REACT_APP_LS_VARIANT_ID_MONTHLY,
      yearly: process.env.REACT_APP_LS_VARIANT_ID_YEARLY,
    },
  },
];

/**
 * AI credit top-up packs (Pro only). Grants are credit points (1 credit =
 * $0.01 of AI usage) and are identical regardless of purchase currency or
 * platform; prices are the GBP stickers (native stores localize their own).
 * Positions must match the API's LS_VARIANT_IDS_CREDITS /
 * RC_PRODUCT_IDS_CREDITS CSVs and CREDIT_SKUS order.
 */
export interface CreditPack {
  sku: "credits_5" | "credits_10" | "credits_20";
  priceGbp: number;
  credits: number;
  rcProductId: string | undefined;
}

export const CREDIT_PACKS: CreditPack[] = [
  {
    sku: "credits_5",
    priceGbp: 5,
    credits: 200,
    rcProductId: process.env.REACT_APP_RC_PRODUCT_ID_CREDITS_5,
  },
  {
    sku: "credits_10",
    priceGbp: 10,
    credits: 400,
    rcProductId: process.env.REACT_APP_RC_PRODUCT_ID_CREDITS_10,
  },
  {
    sku: "credits_20",
    priceGbp: 20,
    credits: 800,
    rcProductId: process.env.REACT_APP_RC_PRODUCT_ID_CREDITS_20,
  },
];

/** The tier (and billing interval) a Lemon Squeezy variant id belongs to. */
export const getTierByVariantId = (
  variantId?: string | null,
): { tier: PricingTier; interval: BillingInterval } | undefined => {
  if (!variantId) return undefined;
  for (const tier of PRICING_TIERS) {
    if (tier.variantIds.monthly === variantId) return { tier, interval: "monthly" };
    if (tier.variantIds.yearly === variantId) return { tier, interval: "yearly" };
  }
  return undefined;
};

/** The native plan a store (RevenueCat) product id belongs to. */
export const getNativePlanByRcProductId = (
  productId?: string | null,
): NativePlan | undefined => {
  if (!productId) return undefined;
  // The exit-offer product IS the yearly term everywhere except the exit
  // sheet itself — Account settings should render it as "(Yearly)".
  if (productId === EXIT_OFFER.rcProductId) {
    return NATIVE_PLANS.find((plan) => plan.interval === "annual");
  }
  return NATIVE_PLANS.find((plan) => plan.rcProductId === productId);
};

/**
 * Saving on a term against paying the monthly price for the same span, as a
 * whole percent. Always derived: a hardcoded label goes stale the moment a
 * store price moves.
 */
export const nativeSavingPercent = (
  monthlyPrice: number,
  termPrice: number,
  months: number,
): number => Math.round((1 - termPrice / (monthlyPrice * months)) * 100);

/**
 * Lifetime is retired from sale but two grandfathered buyers keep access.
 * They never see plan-switch or top-up purchase surfaces (their extra usage
 * is granted via support overrides).
 */
export const isLifetimeVariant = (variantId?: string | null): boolean =>
  !!variantId && variantId === process.env.REACT_APP_LS_VARIANT_ID_LIFETIME;

/**
 * Format price for display in GBP
 */
export const formatPrice = (price: number, currency: string = "GBP"): string => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};

/**
 * Format a whole-pound price with no decimals (e.g. "£99") for the yearly headline.
 */
export const formatPriceWhole = (
  price: number,
  currency: string = "GBP",
): string => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};
